import React from "react";
import { PublicOfferData } from "./BaseTemplate";
import TimeDisplay from "../offer-wizard/TimeDisplay";
import {
  CalendarIcon,
  FoodIcon,
  ConferenceIcon,
  BedIcon,
  GridIcon,
} from "../icons";

interface PublicPlanSummaryProps {
  offer: PublicOfferData;
  template?: string;
}

const PublicPlanSummary: React.FC<PublicPlanSummaryProps> = ({ offer }) => {
  const formatDateWithDay = (dateString: string) => {
    const date = new Date(dateString);
    const dayName = date.toLocaleDateString("pl-PL", { weekday: "long" });
    const formatDate = (dateString: string) => {
      return new Date(dateString).toLocaleDateString("pl-PL", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    };
    const formattedDate = formatDate(dateString);
    return `${dayName}, ${formattedDate}`;
  };

  const getItemIcon = (optionType: string) => {
    switch (optionType) {
      case "gastronomy":
        return <FoodIcon className="w-4 h-4 " />;
      case "conference":
        return <ConferenceIcon className="w-4 h-4 " />;
      case "accommodation":
        return <BedIcon className="w-4 h-4 " />;
      default:
        return <GridIcon className="w-4 h-4 " />;
    }
  };

  return (
    <div className="mb-8" data-section="plan-summary">
      {/* Header */}
      <div className="mb-6">
        <h2
          className="text-xl font-semibold mb-4 flex items-center gap-2"
          style={{ color: "var(--header-color, #334155)" }}
        >
          <CalendarIcon
            className="w-5 h-5"
            style={{ color: "var(--header-color, #64748b)" }}
          />
          Plan Pobytu
        </h2>
      </div>

      {/* Days Timeline */}
      <div className="space-y-6">
        {offer.offerDays?.map((day, index) => (
          <div
            key={day.id || index}
            className="bg-white rounded-xl border border-slate-200 overflow-hidden"
          >
            {/* Day Header */}
            <div
              className="px-6 py-2 border-b border-slate-200"
              style={{
                background: `linear-gradient(90deg, var(--day-background, #f1f5f9) 0%, var(--day-background-end, #e2e8f0) 100%)`,
              }}
            >
              <div className="text-left flex flex-row justify-between w-full items-center">
                <div className="font-bold text-slate-900 text-lg">
                  Dzień {day.dayNumber}
                </div>
                <div className="text-slate-600 text-sm">
                  {formatDateWithDay(day.date)}
                </div>
              </div>
            </div>

            {/* Day Content */}
            <div className="p-6">
              {day.planItems
                ?.filter(
                  (item) => item?.categoryOption?.optionType !== "accommodation"
                )
                .map((item, itemIndex: number) => (
                  <div
                    key={item.id || itemIndex}
                    className="flex gap-4 items-start py-4 border-b border-slate-100 last:border-b-0"
                  >
                    {/* Time */}
                    <div className="flex items-center gap-2 min-w-[140px]">
                      <TimeDisplay
                        timeFrom={item.timeFrom}
                        timeTo={item.timeTo}
                        className="text-sm font-medium text-slate-700"
                      />
                    </div>

                    {/* Icon */}
                    <div className="flex items-start mt-1">
                      {getItemIcon(item.categoryOption?.optionType || "")}
                    </div>

                    {/* Content */}
                    <div className="flex-1 text-left">
                      <div className="font-semibold text-slate-900 mb-1">
                        {item.customTitle || item.categoryOption?.name}
                      </div>
                      {item.notes && (
                        <div className="text-sm text-slate-600 leading-relaxed">
                          {item.notes}
                        </div>
                      )}
                      {item.quantity && item.quantity > 1 && (
                        <div className="text-xs text-slate-500 mt-1">
                          Ilość: {item.quantity}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

              {/* Empty state */}
              {(!day.planItems ||
                day.planItems.filter(
                  (item) => item?.categoryOption?.optionType !== "accommodation"
                ).length === 0) && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <CalendarIcon className="w-8 h-8 text-slate-400" />
                  </div>
                  <div className="text-slate-500 font-medium">
                    Brak zaplanowanych elementów
                  </div>
                  <div className="text-sm text-slate-400 mt-1">
                    W tym dniu nie ma jeszcze żadnych aktywności
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Empty state for no days */}
        {(!offer.offerDays || offer.offerDays.length === 0) && (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CalendarIcon className="w-10 h-10 text-slate-400" />
            </div>
            <div className="text-lg font-medium text-slate-600 mb-2">
              Brak zaplanowanych dni
            </div>
            <div className="text-slate-500">
              Plan pobytu zostanie wygenerowany po wypełnieniu podstawowych
              informacji
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicPlanSummary;
